<script>
    import { getContext, setContext } from 'svelte';
    import { writable } from 'svelte/store';
    import { json } from 'd3-fetch';
    import { initialize_selector } from './util/StopSelector.js';
    import { median } from 'd3-array';

    // --- Properties

    export let databaseKey;
    const { retrieve, query, aql } = getContext(databaseKey);

    // --- UI layers
    const showPOIs = writable(true);
    const showStops = writable(true);

    // --- Filter
    const speed = writable(20);
    const duration = writable(30);
    const distance = writable(4000);
    const missing_distance = writable(220);

    const enable_timeline = writable(false);
    const car = writable(1);

    // --- Data
    const cars = retrieve(aql`
        for t in jb_trajectories
            return distinct t.car
    `);
    const streets = writable({});
    json('./Abila.geojson').then((res) => {
        $streets = res;
    });
    const stops = writable({});
    $: query(
        aql`
        FOR t in jb_trajectories
            FILTER t.duration > ${$duration}
            FILTER t.speed < ${$speed}
            FILTER t.distance < ${$distance}
            COLLECT car = t.car into g = t
            SORT g.start.timestamp
            FOR i in 0..LENGTH(g)-1
                FILTER i < 1 || g[i-1].stop.timestamp != g[i].start.timestamp
                LET combine = i < LENGTH(g)-1 && g[i].stop.timestamp == g[i+1].start.timestamp
                LET start = g[i].start
                LET stop = combine ? g[i+1].stop : g[i].stop
                LET distance = combine ? g[i].distance + g[i+1].distance : g[i].distance
                LET duration = combine ? g[i].duration + g[i+1].duration : g[i].duration
                LET speed = combine ? distance / duration * 3.6 : g[i].speed
                LET state = distance > ${$missing_distance} ? 'rejected' : 'accepted'
                RETURN {
                    _key: concat(car, '_', start.timestamp),
                    start, stop, distance, duration, speed, state, car
                }
    `,
        stops,
        (res) =>
            res.reduce((agg, v) => {
                agg[v._key] = v;
                return agg;
            }, {})
    );
    $: positions = Object.keys($stops)
        .map((k) => {
            const s = $stops[k];
            return [
                { ...s.start, _key: s._key },
                { ...s.stop, _key: s._key },
            ];
        })
        .flat();

    // --- Selector
    const selector = initialize_selector();
    const selected_item = selector.selected_item;

    // --- Timeline
    const time_begin = 1388966400000;
    const time_end = 1390176000000;
    const moving_time = writable(false);
    const time_range = writable([time_begin, time_end]);

    // --- POI annotation
    const pois = writable([]);
    const poi_distance = writable(100);

    function save_poi() {
        const selected_ps = positions.filter(
            (s) =>
                geo_distance(
                    s.lat,
                    s.long,
                    $selected_item.lat,
                    $selected_item.long
                ) < $poi_distance
        );
        const lat = median(selected_ps, (p) => p.lat);
        const long = median(selected_ps, (p) => p.long);
        const keys = [...new Set(selected_ps.map((p) => p._key))];
        $pois = [
            ...$pois,
            {
                _key: `${lat}_${long}`,
                lat,
                long,
                stops: keys,
                type: '',
                locations: [],
            },
        ];
        selector.select(null);
    }

    function geo_distance(lat1, lon1, lat2, lon2) {
        const p = 0.017453292519943295; // Math.PI / 180
        const c = Math.cos;
        const a =
            0.5 -
            c((lat2 - lat1) * p) / 2 +
            (c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p))) / 2;
        const res = 12742 * Math.asin(Math.sqrt(a)); // 2 * R; R = 6371 km
        return res * 1000; // meter
    }
    // --- Trajectory
    const trajectory = writable();

    // --- Export values
    async function export_values() {
        console.warn('export is disabled');
    }

    // --- Context
    const key = {};
    setContext(key, {
        streets,
        stops,
        cars,
        car,
        enable_timeline,
        speed,
        duration,
        distance,
        missing_distance,
        selector,
        moving_time,
        time_range,
        trajectory,
        aql,
        query,
        poi_distance,
        pois,
        save_poi,
        geo_distance,
        showPOIs,
        showStops,
        export_values,
    });
</script>

<slot {key} />
